import { Component } from "react";

/**
 * Componente para detección de código de barras mediante escáner físico
 * 
 * @example
 * // En tu componente de clase:
 * componentDidMount() {
 *   this.barcodeScanner = new BarcodeScanner({
 *     onScan: this.handleBarcodeScan.bind(this),
 *     minScanLength: 4,
 *     scanGapMs: 25,
 *     fastKeysToArm: 3,
 *     idleTimeoutMs: 80
 *   });
 * }
 * 
 * componentWillUnmount() {
 *   this.barcodeScanner?.destroy();
 * }
 * 
 * handleBarcodeScan(codigo) {
 *   console.log('Código escaneado:', codigo);
 * }
 */
class BarcodeScanner {
  constructor(options = {}) {
    // Configuración
    this.config = {
      onScan: options.onScan || (() => {}),
      minScanLength: options.minScanLength || 4,
      scanGapMs: options.scanGapMs || 25,
      fastKeysToArm: options.fastKeysToArm || 3,
      idleTimeoutMs: options.idleTimeoutMs || 80,
      preventScanWhenLoading: options.preventScanWhenLoading !== false,
      getLoadingState: options.getLoadingState || (() => false),
    };

    // Estado interno
    this.buffer = "";
    this.tempBuffer = [];
    this.lastTs = 0;
    this.fastCount = 0;
    this.scanning = false;
    this.idleTimer = null;

    // Bind del listener
    this.eventListenerKeyboards = this.eventListenerKeyboards.bind(this);
    
    // Iniciar escucha
    this.init();
  }

  init() {
    document.addEventListener("keydown", this.eventListenerKeyboards, { capture: true });
  }

  destroy() {
    document.removeEventListener("keydown", this.eventListenerKeyboards, { capture: true });
    clearTimeout(this.idleTimer);
    this.reset();
  }

  reset() {
    this.buffer = "";
    this.tempBuffer = [];
    this.fastCount = 0;
    this.scanning = false;
    clearTimeout(this.idleTimer);
  }

  eventListenerKeyboards(e) {
    const tag = (e.target && e.target.tagName) || "";
    const isTypingField = /INPUT|TEXTAREA|SELECT/.test(tag);
    const key = e.key;
    const isPrintable = key && key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    const isEndKey = key === "Enter" || key === "Tab";
    
    if (!isPrintable && !isEndKey) return;

    const now = performance.now();
    const delta = now - (this.lastTs || now);
    this.lastTs = now;

    // -------- NO ESCANEANDO AÚN --------
    if (!this.scanning) {
      if (isPrintable) {
        // Si hubo pausa, empezamos nueva racha
        if (delta >= this.config.scanGapMs) {
          this.fastCount = 0;
          this.tempBuffer = [];
        } else {
          this.fastCount += 1;
        }

        // SIEMPRE guarda la tecla en tempBuffer
        this.tempBuffer.push(key);

        // ¿Armamos el escáner?
        if (this.fastCount >= this.config.fastKeysToArm - 1) {
          this.scanning = true;

          // Bloquea escritura desde ESTA tecla en adelante
          e.preventDefault();
          e.stopPropagation();

          // Si veníamos tecleando en un input, borra lo ya escrito
          if (isTypingField && e.target && typeof e.target.value === "string") {
            try {
              const el = e.target;
              const len = this.tempBuffer.length;
              const end = el.selectionEnd ?? el.value.length;
              const start = Math.max(0, end - len);
              el.value = el.value.slice(0, start) + el.value.slice(end);
              if (el.setSelectionRange) el.setSelectionRange(start, start);
              el.dispatchEvent(new Event("input", { bubbles: true }));
            } catch {}
            if (e.target?.blur) e.target.blur();
          }

          // Siembra el buffer con TODO lo capturado
          this.buffer = this.tempBuffer.join("");

          clearTimeout(this.idleTimer);
          this.idleTimer = setTimeout(() => this.finishIfScan(), this.config.idleTimeoutMs);
          return;
        }

        // Aún no armado → deja pasar la escritura normal
        return;
      }
      return;
    }

    // -------- YA EN MODO ESCÁNER --------
    if (isPrintable) {
      e.preventDefault();
      e.stopPropagation();
      this.buffer += key;
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.finishIfScan(), this.config.idleTimeoutMs);
      return;
    }
    
    if (isEndKey) {
      e.preventDefault();
      e.stopPropagation();
      this.finishIfScan();
      return;
    }
  }

  finishIfScan() {
    clearTimeout(this.idleTimer);
    const code = (this.buffer || "").trim();

    // Reset estado
    const wasScanning = this.scanning;
    this.buffer = "";
    this.tempBuffer = [];
    this.fastCount = 0;
    this.scanning = false;

    // Verificar condiciones para procesar el código
    if (
      wasScanning &&
      code.length >= this.config.minScanLength &&
      (!this.config.preventScanWhenLoading || !this.config.getLoadingState())
    ) {
      this.config.onScan(code);
    }
  }

  // Método público para actualizar configuración
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  // Método público para obtener estado actual
  isScanning() {
    return this.scanning;
  }
}

export default BarcodeScanner;


/**
 * HOC (Higher Order Component) para usar el escáner en componentes de clase
 * 
 * @example
 * import { withBarcodeScanner } from './BarcodeScanner';
 * 
 * class MiComponente extends Component {
 *   handleScan = (codigo) => {
 *     console.log('Escaneado:', codigo);
 *   }
 * 
 *   render() {
 *     return <div>Mi contenido</div>;
 *   }
 * }
 * 
 * export default withBarcodeScanner(MiComponente, {
 *   onScan: 'handleScan', // nombre del método que manejará el escaneo
 *   minScanLength: 4
 * });
 */
export function withBarcodeScanner(WrappedComponent, scannerConfig = {}) {
  return class extends Component {
    componentDidMount() {
      const onScanMethod = scannerConfig.onScan || 'handleBarcodeScan';
      
      this.barcodeScanner = new BarcodeScanner({
        ...scannerConfig,
        onScan: (codigo) => {
          if (this.wrappedRef && typeof this.wrappedRef[onScanMethod] === 'function') {
            this.wrappedRef[onScanMethod](codigo);
          }
        },
        getLoadingState: () => {
          return this.wrappedRef?.state?.loading || false;
        }
      });
    }

    componentWillUnmount() {
      this.barcodeScanner?.destroy();
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          ref={(ref) => (this.wrappedRef = ref)}
          barcodeScanner={this.barcodeScanner}
        />
      );
    }
  };
}